import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MapPin, Calendar, Users, Sparkles } from 'lucide-react';
export const EventDetailsSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section id="details" ref={sectionRef} className="py-20 md:py-28 bg-black relative overflow-hidden" style={{
    opacity
  }} data-id="element-1180">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black/60 z-0" data-id="element-1181"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1182">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-24" data-id="element-1183">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1184">
            Essential Information
          </p>
          <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-1185">
            EVENT
            <br data-id="element-1186" />
            <span className="text-amber-400" data-id="element-1187">DETAILS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-1188"></div>
        </motion.div>
        <div className="max-w-6xl mx-auto" data-id="element-1189">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-1190">
            {/* Venue Card */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1191">
              <div className="relative" data-id="element-1192">
                <div className="aspect-video relative overflow-hidden mb-8 rounded-lg" data-id="element-1193">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kppHXez1XGBnRYW5bwUDVX/illumiarium.png" alt="Illuminarium Venue" className="w-full h-full object-contain bg-black" data-id="element-1194" />
                </div>
                <div className="space-y-8" data-id="element-1195">
                  <div className="flex items-start" data-id="element-1196">
                    <MapPin className="w-6 h-6 text-amber-400 mr-4 flex-shrink-0 mt-1" data-id="element-1197" />
                    <div data-id="element-1198">
                      <h4 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1199">
                        Venue
                      </h4>
                      <p className="text-white/80 font-light leading-relaxed" data-id="element-1200">
                        Illuminarium Toronto
                        <br data-id="element-1201" />
                        123 Experience Drive
                        <br data-id="element-1202" />
                        Toronto, ON M5V 2K4
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1203">
                    <Calendar className="w-6 h-6 text-amber-400 mr-4 flex-shrink-0 mt-1" data-id="element-1204" />
                    <div data-id="element-1205">
                      <h4 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1206">
                        Dates
                      </h4>
                      <p className="text-white/80 font-light leading-relaxed" data-id="element-1207">
                        Summer 2026
                        <br data-id="element-1208" />
                        Doors open at 6:00 PM
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            {/* Details Card */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex flex-col justify-between space-y-8" data-id="element-1209">
              <div className="space-y-8" data-id="element-1210">
                <div className="flex items-start" data-id="element-1211">
                  <Users className="w-6 h-6 text-amber-400 mr-4 flex-shrink-0 mt-1" data-id="element-1212" />
                  <div data-id="element-1213">
                    <h4 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1214">
                      Capacity
                    </h4>
                    <p className="text-white/80 font-light leading-relaxed" data-id="element-1215">
                      Limited & exclusive seating
                      <br data-id="element-1216" />
                      Only 120 guests per evening
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-1217">
                  <Sparkles className="w-6 h-6 text-amber-400 mr-4 flex-shrink-0 mt-1" data-id="element-1218" />
                  <div className="flex-1" data-id="element-1219">
                    <h4 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1220">
                      Format
                    </h4>
                    <div className="relative bg-gradient-to-br from-amber-400/10 to-amber-400/5 border-l-2 border-amber-400 p-5 rounded-r-lg" data-id="element-1221">
                      <span className="inline-block text-[10px] tracking-[0.25em] uppercase text-amber-400 font-medium mb-3 px-2 py-1 bg-amber-400/10 border border-amber-400/30 rounded" data-id="element-1222">
                        Walk-Through Cinematic Journey
                      </span>
                      <p className="text-white font-light leading-relaxed mb-3" data-id="element-1223">
                        Step into the court of{' '}
                        <span className="text-amber-400 font-medium" data-id="element-1224">
                          Maharaja Ranjit Singh
                        </span>
                        . A 60-minute immersive walk through the lost world of
                        the Sikh Empire — brought alive through cinematic
                        visuals, live performance, and interactive moments.
                      </p>
                      <p className="text-white/60 text-sm font-light italic" data-id="element-1225">
                        Think Universal Studios meets the Wizarding World — but
                        rooted in the heritage of Punjab.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-amber-400/30 p-8 bg-black/40" data-id="element-1226">
                <h3 className="font-serif text-3xl text-amber-400 mb-4" data-id="element-1227">
                  Summer 2026
                </h3>
                <p className="text-white/70 font-light mb-4 leading-relaxed" data-id="element-1228">
                  The exclusive experience takes place Summer 2026. All ticket
                  holders receive admission to this premiere experience.
                </p>
                <div className="w-full h-px bg-white/10 my-6" data-id="element-1229"></div>
                <h4 className="text-xl text-amber-400 font-light mb-3" data-id="element-1230">
                  Monthly Experiences
                </h4>
                <p className="text-white/70 font-light mb-6 leading-relaxed" data-id="element-1231">
                  Your ticket also grants access to one monthly experience
                  between May and December 2026. Choose your preferred date when
                  finalizing your reservation.
                </p>
                <div className="flex justify-between items-center pt-4 border-t border-white/10" data-id="element-1232">
                  <span className="text-amber-400 font-light tracking-wider" data-id="element-1233">
                    Summer 2026
                  </span>
                  <span className="text-white/60 text-sm tracking-wider uppercase" data-id="element-1234">
                    Premiere Event
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3" data-id="element-1235">
                  <span className="text-amber-400 font-light tracking-wider" data-id="element-1236">
                    May - Dec 2026
                  </span>
                  <span className="text-white/60 text-sm tracking-wider uppercase" data-id="element-1237">
                    Monthly Access
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        {/* Bottom Quote */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="text-center mt-24" data-id="element-1238">
          <p className="text-xl text-white/70 italic font-light max-w-3xl mx-auto" data-id="element-1239">
            "Step inside a kingdom reborn — where history, sense, and story
            collide for one unforgettable evening."
          </p>
        </motion.div>
      </div>
    </motion.section>;
};